/**
 * Utility functions for calculating vegetation value adjustments
 * Used when creating contracts to account for existing crops/trees
 */

import { ExistingVegetationItem } from '@/types/plot';

/**
 * Calculate the rent adjustment based on existing bearing vegetation
 * Returns 25% of the estimated annual value of all bearing crops/trees
 *
 * @param vegetation - Array of vegetation items
 * @returns Total vegetation value adjustment in NGN
 */
export const calculateVegetationValueAdjustment = (
  vegetation: ExistingVegetationItem[] | undefined
): number => {
  if (!vegetation || vegetation.length === 0) {
    return 0;
  }

  return vegetation.reduce((total, item) => {
    // Only bearing crops contribute to rent adjustment
    if (item.bearing_status === 'bearing' && item.estimated_total_annual_value) {
      // Landlord gets approximately 25% of annual value as additional rent
      // This reflects the value of existing productive assets on the land
      return total + (item.estimated_total_annual_value * 0.25);
    }
    return total;
  }, 0);
};

/**
 * Create a snapshot of vegetation data for contract
 * This preserves the state of vegetation at the time of contract creation
 *
 * @param vegetation - Current vegetation data from plot
 * @returns Deep copy of vegetation data
 */
export const createVegetationSnapshot = (
  vegetation: ExistingVegetationItem[] | undefined
): ExistingVegetationItem[] | undefined => {
  if (!vegetation || vegetation.length === 0) {
    return undefined;
  }

  // Create a deep copy with current timestamp
  return vegetation.map(item => ({
    ...item,
    last_assessed: new Date().toISOString().split('T')[0],
  }));
};

/**
 * Calculate total contract amount including base rent and vegetation adjustment
 *
 * @param baseAmount - Base rent amount
 * @param vegetation - Vegetation items from plot
 * @returns Total amount including vegetation value adjustment
 */
export const calculateTotalContractAmount = (
  baseAmount: number,
  vegetation: ExistingVegetationItem[] | undefined
): number => {
  const vegetationAdjustment = calculateVegetationValueAdjustment(vegetation);
  return baseAmount + vegetationAdjustment;
};

/**
 * Get summary of vegetation value for display
 *
 * @param vegetation - Array of vegetation items
 * @returns Object with count and total value
 */
export const getVegetationValueSummary = (
  vegetation: ExistingVegetationItem[] | undefined
): { bearingCount: number; totalAnnualValue: number; rentAdjustment: number } => {
  if (!vegetation || vegetation.length === 0) {
    return { bearingCount: 0, totalAnnualValue: 0, rentAdjustment: 0 };
  }

  const bearingItems = vegetation.filter(item => item.bearing_status === 'bearing');
  const totalAnnualValue = bearingItems.reduce(
    (sum, item) => sum + (item.estimated_total_annual_value || 0),
    0
  );
  const rentAdjustment = calculateVegetationValueAdjustment(vegetation);

  return {
    bearingCount: bearingItems.length,
    totalAnnualValue,
    rentAdjustment,
  };
};
